import { ActivityController, ActivityReport } from '@app/api/activity-report/models/activity-report.models';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { merge, Observable, of } from 'rxjs';
import { ActivityReportApiService } from '@app/api/activity-report/activity-report-api.service';
import { BroadcastService } from '@app/common/services/broadcast.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class ActivityReportManagerService {
    private readonly destroyRef = inject(DestroyRef);

    private activityReportApi = inject(ActivityReportApiService);
    private broadcastService = inject(BroadcastService);

    private cachedReport: ActivityReport | null = null;
    /** Which request the cached report answers. Null whenever there is no cached report. */
    private cachedReportKey: string | null = null;

    constructor() {
        // Root singleton, so it outlives a session. Logout is an in-app navigation and a session
        // timeout only routes to /login from the 401 handler, so the page is never reloaded and a
        // report built for one user would otherwise sit here for the next one (AB#23853).
        merge(this.broadcastService.userLoggedIn, this.broadcastService.userLoggedOut)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.clearCache());
    }

    // =========================================================================================================================================================
    // Public Methods
    // =========================================================================================================================================================

    getActivityReport(accountId: string, startDate: string, endDate: string, limit: number = 20, cursor: string | null = null, bypassCache = false)
        : Observable<ActivityReport> {
        // The key is the whole request, not just the account. A cache that ignores its arguments
        // answers every caller with the first report it ever saw - wrong account, wrong date range
        // and wrong page size alike.
        const key = [accountId, startDate, endDate, limit].join('|');

        if (!bypassCache && this.cachedReport != null && this.cachedReportKey === key && !cursor) {
            return of(this.cachedReport);
        }

        return this.activityReportApi.getActivityReport(accountId, startDate, endDate, limit, cursor)
            .pipe(
                tap((report: ActivityReport) => {
                    if (!cursor) {
                        this.cachedReport = report;
                        this.cachedReportKey = key;
                    }
                })
            );
    }

    /**
     * One page of the report for the header Activity panel (AB#20613). Never reads or writes the cache: the panel
     * asks for a fresh "last 7 days" window every time, and caching it would push out the report page's own entry.
     */
    getActivityPage(accountId: string, startDate: string, endDate: string, limit: number, cursor: string | null = null)
        : Observable<ActivityReport> {
        return this.activityReportApi.getActivityReport(accountId, startDate, endDate, limit, cursor);
    }

    clearCache(): void {
        this.cachedReport = null;
        this.cachedReportKey = null;
    }

    extractControllers(report: ActivityReport): ActivityController[] {
        if (!report || !report.activities) {
            return [];
        }

        const controllerMap = new Map<string, ActivityController>();

        for (const activity of report.activities) {
            if (activity.controller && activity.controller.id) {
                if (!controllerMap.has(activity.controller.id)) {
                    controllerMap.set(activity.controller.id, activity.controller);
                }
            }
        }

        return Array.from(controllerMap.values());
    }
}
